import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { LoadingSpinner } from "@/shared/components/loading-spinner";
import { QuizStepHeader } from "./quiz-step-header";
import { ClaimCard } from "./claim-card";
import { SortMenu } from "./sort-menu";
import { useSettlements } from "../hooks/use-settlements";
import { useClaimsStore } from "../store";
import {
  claimsPayoutUnknownCount,
  claimsProgressBarClass,
  claimsProgressGoalUsd,
  claimsProgressLabel,
  sumPayoutPotentialUsd,
  toSelectedSettlement,
} from "../domain/payout";
import type { ClaimsSortId } from "../types";
import sortIcon from "../assets/icon-sort.svg";
import searchClearIcon from "../assets/icon-search-clear.svg";
import searchEmptyLens from "../assets/icon-search-empty-lens.svg";
import searchEmptyHandle from "../assets/icon-search-empty-handle.svg";

function SearchEmptyIllustration() {
  return (
    <div className="relative h-16 w-24 overflow-hidden">
      <div className="absolute left-[calc(50%-28px)] top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 rounded-[5px] bg-[#19191C]/10" />
      <div className="absolute left-1/2 top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 rounded-[5px] bg-[#19191C]/10" />
      <div className="absolute left-[calc(50%+28px)] top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 rounded-[5px] bg-[#19191C]/10" />
      <div className="absolute left-6 top-2 size-12">
        <span className="absolute inset-[-4.17%] overflow-hidden">
          <img src={searchEmptyLens} alt="" className="size-full" />
        </span>
      </div>
      <div className="absolute left-[66px] top-[50px] size-[7px]">
        <span className="absolute inset-[-28.57%] overflow-hidden">
          <img src={searchEmptyHandle} alt="" className="size-full" />
        </span>
      </div>
    </div>
  );
}

interface ClaimsStepProps {
  onBack: () => void;
  onAdvance: () => void;
}

export function ClaimsStep({ onBack, onAdvance }: ClaimsStepProps) {
  const [query, setQuery] = useState("");
  const [sortId, setSortId] = useState<ClaimsSortId>("featured");
  const [sortOpen, setSortOpen] = useState(false);
  const { items, loading, error } = useSettlements(query, sortId);
  const selected = useClaimsStore((s) => s.selected);
  const toggleSelected = useClaimsStore((s) => s.toggleSelected);

  const selectedIds = useMemo(
    () => new Set(selected.map((s) => s.id)),
    [selected],
  );
  const total = sumPayoutPotentialUsd(selected);
  const unknownCount = claimsPayoutUnknownCount(selected);
  const goal = claimsProgressGoalUsd(total);
  const canContinue =
    selected.length > 0 ||
    (!query.trim() && !loading && !error && items.length === 0);
  const progress = Math.min(1, Math.max(0, total / goal));

  return (
    <div className="flex h-[100dvh] flex-col bg-mf-neutral-50 font-body text-mf-neutral-900">
      <div className="relative min-h-0 flex-1">
        <div className="h-full overflow-y-auto">
          <QuizStepHeader onBack={onBack} />
          <div className="flex flex-col gap-4 px-4 pb-4">
            <div className="flex flex-col gap-2 text-center">
              <h1 className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
                Your Available Claims
              </h1>
              <p className="text-base leading-5 text-mf-neutral-600">
                Some are active settlements ready to file, others are pending or
                potential arbitration. Each has its own eligibility rules and
                timeline.
              </p>
            </div>

            <div className="relative flex items-center gap-2" data-sort-root>
              <div className="flex h-11 flex-1 items-center gap-1 rounded-full bg-white py-2 pl-3 pr-2 shadow-[0_0_16px_rgba(26,22,19,0.03)]">
                <Search
                  className="size-6 shrink-0 text-mf-neutral-400"
                  strokeWidth={1.75}
                />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search claims"
                  className="h-full min-w-0 flex-1 appearance-none bg-transparent text-base leading-5 text-mf-neutral-900 outline-none placeholder:text-[#c0b7ae]"
                />
                {query.length > 0 && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setQuery("");
                    }}
                    className="relative flex size-6 shrink-0 items-center justify-center overflow-hidden"
                  >
                    <img src={searchClearIcon} alt="" className="size-[22px]" />
                  </button>
                )}
              </div>
              <button
                type="button"
                aria-label="Sort claims"
                aria-expanded={sortOpen}
                onClick={() => setSortOpen((open) => !open)}
                className={cn(
                  "flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_0_16px_rgba(26,22,19,0.03)]",
                  sortOpen && "border-2 border-mf-green",
                )}
              >
                <span className="flex size-6 items-center justify-center overflow-hidden">
                  <img src={sortIcon} alt="" className="size-5" />
                </span>
              </button>
              <SortMenu
                open={sortOpen}
                onClose={() => setSortOpen(false)}
                value={sortId}
                onChange={setSortId}
              />
            </div>

            {loading && (
              <div className="flex flex-1 items-center justify-center py-16">
                <LoadingSpinner />
              </div>
            )}

            {!loading && error && (
              <p className="py-8 text-center text-sm text-mf-error">{error}</p>
            )}

            {!loading && !error && items.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2">
                <SearchEmptyIllustration />
                <p className="w-full text-center text-lg font-bold leading-6 text-mf-neutral-900">
                  {query.trim()
                    ? "No claims match your search"
                    : "No popular claims available right now"}
                </p>
              </div>
            )}

            {!loading && items.length > 0 && (
              <div className="flex flex-col gap-3">
                {items.map((item) => (
                  <ClaimCard
                    key={item.id}
                    item={item}
                    selected={selectedIds.has(item.id)}
                    onToggle={() => toggleSelected(toSelectedSettlement(item))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-b from-transparent to-mf-neutral-50" />
      </div>

      <div className="flex shrink-0 items-center gap-4 bg-white px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-col">
            <p className="text-[11px] leading-[14px] text-mf-neutral-400">
              {selected.length} {selected.length === 1 ? "claim" : "claims"}{" "}
              selected
            </p>
            <p className="font-display text-xl font-bold leading-6 text-mf-neutral-900">
              {claimsProgressLabel(total, unknownCount, goal)}
            </p>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-mf-neutral-100">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                claimsProgressBarClass(progress),
              )}
              style={{
                width: progress > 0 ? `max(6px, ${progress * 100}%)` : 0,
              }}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onAdvance}
          disabled={!canContinue}
          className={cn(
            "flex h-12 w-[144px] shrink-0 items-center justify-center rounded-full px-4 text-[15px] font-bold text-white",
            canContinue
              ? "bg-mf-neutral-900"
              : "cursor-not-allowed bg-mf-neutral-900/20",
          )}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
